import { __assign, __read } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { getFileExtensionFromFileTypeFull, getFileExtensionFromName, getFileTypeFromFileName, getFileTypeNameFromType, } from '@office-start/file-utility';
import { launchContent } from '@office-start/launcher-service';
import { createPromiseCancellationToken } from '@office-start/models-utility';
import { useShowStorageErrorDialog } from '@office-start/storage-error-service';
import { logFeature } from '@office-start/telemetry-service';
import { UploadErrorType, UploadSource, UploadStatus, } from '@office-start/upload-types';
import { useEffect, useMemo } from 'react';
import { useFilePicker } from 'use-file-picker';
import { getCommonTelemetryParams, getSanitizedError } from '../utilities';
import { useUploadConfig } from './useUploadConfig';
import { useUploadFile } from './useUploadFile';
import { useUploadState } from './useUploadState';
export function useUploadCallback(area, source, version) {
    if (source === void 0) { source = UploadSource.Button; }
    var uploadFile = useUploadFile();
    var uploadConfig = useUploadConfig().data;
    var officeComponentContext = useOfficeComponentContext().data;
    var _a = __read(useShowStorageErrorDialog(), 2), showStorageErrorDialog = _a[0], shouldShowStorageErrorDialog = _a[1];
    var featureGates = useFeatureGates().data;
    var uploadFileTypes = Array.from((uploadConfig === null || uploadConfig === void 0 ? void 0 : uploadConfig.uploadFileTypes.values()) || [])
        .map(function (fileType) { return getFileExtensionFromFileTypeFull(fileType) || []; })
        .reduce(function (a, b) { return a.concat(b); }, [])
        .filter(function (item) { return item !== ''; })
        .map(function (extension) { return '.' + extension; });
    var _b = useUploadState(), uploadState = _b.uploadState, setV1FileUploadState = _b.setV1FileUploadState;
    var _c = __read(useFilePicker({
        multiple: false,
        accept: uploadFileTypes,
        readAs: 'ArrayBuffer', // available formats: "Text" | "BinaryString" | "ArrayBuffer" | "DataURL"
    }), 4), filesContent = _c[0], errors = _c[1], openFileSelector = _c[2], loading = _c[3];
    useEffect(function () {
        var _a, _b;
        var _c, _d;
        var params = getCommonTelemetryParams(source, version);
        if (errors.length) {
            logFeature({
                featureName: "Upload" /* KnownFeatureNames.Upload */,
                eventName: "File_Picker_Error" /* KnownUploadInstrumentationEvents.FilePickerError */,
                params: __assign(__assign({}, params), (_a = {}, _a["Error" /* InstrumentationParam.Error */] = getSanitizedError(errors), _a)),
            });
        }
        if (!(errors && errors.length) &&
            filesContent &&
            filesContent.length &&
            filesContent[0] &&
            !loading) {
            var fileContent = filesContent[0];
            var fileName = fileContent.name;
            var fileContentString = fileContent.content;
            var fileBlob = new Blob([fileContentString], {
                type: 'text/plain',
            });
            var file_1 = new File([fileBlob], fileName);
            var filetype_1 = getFileTypeFromFileName(file_1.name);
            var filetypeName_1 = getFileTypeNameFromType(filetype_1);
            var extension_1 = getFileExtensionFromName(file_1.name);
            if ((((_d = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _c === void 0 ? void 0 : _c.storageErrorGates) === null || _d === void 0 ? void 0 : _d.storageErrorDialogForUploadEnabled) ||
                false) &&
                !!shouldShowStorageErrorDialog &&
                shouldShowStorageErrorDialog(filetypeName_1 === null || filetypeName_1 === void 0 ? void 0 : filetypeName_1.toString())) {
                showStorageErrorDialog({
                    workloadId: filetypeName_1 === null || filetypeName_1 === void 0 ? void 0 : filetypeName_1.toString(),
                    area: 'Upload',
                    subArea: 'Other',
                    isTemplate: false,
                });
            }
            else if (file_1) {
                var fileUploadState = {
                    sequenceNumber: 0,
                    file: file_1,
                    uploadStatus: UploadStatus.Started,
                    cancellationToken: createPromiseCancellationToken(),
                    telemetry: {
                        area: area,
                        source: source,
                        version: version,
                    },
                };
                logFeature({
                    featureName: "Upload" /* KnownFeatureNames.Upload */,
                    eventName: "File_Selected" /* KnownUploadInstrumentationEvents.FileSelected */,
                    params: __assign(__assign({}, params), (_b = {}, _b["DocumentType" /* InstrumentationParam.DocumentType */] = filetype_1, _b["Area" /* InstrumentationParam.Area */] = area, _b)),
                });
                setV1FileUploadState(fileUploadState);
                uploadFile(fileUploadState)
                    .then(function (newFileUploadState) {
                    setV1FileUploadState(undefined, UploadStatus.Completed);
                    if (newFileUploadState &&
                        newFileUploadState.fileUrlModel) {
                        var fileUrlModel = newFileUploadState.fileUrlModel;
                        var document_1 = {
                            canonicalUrl: fileUrlModel.canonicalUrl,
                            webUrl: fileUrlModel.webUrl,
                            app: filetypeName_1,
                            attribution: fileUrlModel.canonicalUrl,
                            url: fileUrlModel.canonicalUrl,
                            extension: extension_1,
                            title: file_1.name,
                            id: fileUrlModel.docId || '',
                            fileType: filetype_1,
                        };
                        launchContent({
                            action: 'Upload',
                            inAppNavFileType: filetype_1,
                            document: document_1,
                            useWebUrl: true,
                            wdOriginType: "UPLOAD" /* WdOriginType.Upload */,
                        });
                    }
                })
                    .catch(function (error) {
                    if (error === UploadErrorType.UnsupportedType) {
                        logFeature({
                            featureName: "Upload" /* KnownFeatureNames.Upload */,
                            eventName: "Unsupported_File_Type" /* KnownUploadInstrumentationEvents.UnsupportedFileType */,
                            params: params,
                        });
                        setV1FileUploadState(undefined, UploadStatus.Unsupported);
                    }
                    else {
                        setV1FileUploadState(undefined, UploadStatus.Failed);
                    }
                });
                setV1FileUploadState(undefined, UploadStatus.Uploading);
            }
        }
    }, [
        errors,
        filesContent,
        loading,
        uploadFile,
        officeComponentContext,
        setV1FileUploadState,
    ]);
    return useMemo(function () { return ({
        v1UploadStatus: uploadState.v1FileUploadState.uploadStatus,
        openFileSelector: openFileSelector,
        resetV1UploadStatus: function () {
            return setV1FileUploadState(undefined, UploadStatus.Started, null);
        },
    }); }, [
        openFileSelector,
        setV1FileUploadState,
        uploadState.v1FileUploadState.uploadStatus,
    ]);
}
//# sourceMappingURL=useUploadCallback.js.map